/**
 * PartViews: the 3D half of each part. A view builds its mesh once and then
 * animates it from simulation state every frame (glow, spin, wobble, flight…).
 *
 * Local space: origin at the part's center, +x points from pin 0 to the last pin,
 * +y is up, the plate's bottom is at y = 0.
 */
import * as THREE from 'three';
import type { Board } from '../core/board';
import type { Placement, PropValue } from '../core/types';
import type { BatteryState, BuzzerState, GlowState, LedState, MotorState } from '../parts/defs';
import { LED_COLORS, MOTOR, type LedColor } from '../parts/defs';
import { partDef } from '../parts/registry';
import type { Reading } from '../sim/sim';
import {
  COLORS,
  LAYER_H,
  PLATE_H,
  PLATE_W,
  WIRE_COLORS,
  chrome,
  cylinder,
  decal,
  glowSprite,
  ownMaterial,
  plastic,
  plate,
  plateLength,
  roundRect,
  slabGeometry,
  snap,
  sphere,
} from './kit';

export interface ViewFrame {
  /** The board, for parts that need to see what's around them (jumper cable ends). */
  board?: Board;
  state: unknown;
  reading: Reading;
  input: Readonly<Record<string, PropValue>>;
  time: number;
  dt: number;
}

export interface Emitter {
  /** World position is read from this object. */
  at: THREE.Object3D;
  color: THREE.Color;
  /** 0…1+ */
  intensity: number;
}

export interface PartView {
  group: THREE.Group;
  emitters: Emitter[];
  /**
   * The path electricity takes through the part, pin 0 → pin 1, in local space.
   * Defaults to a straight line between the snaps.
   */
  flow?: THREE.Curve<THREE.Vector3>;
  update(p: Placement, f: ViewFrame): void;
  dispose(): void;
}

const clamp01 = (x: number) => Math.min(1, Math.max(0, x));
const damp = (cur: number, target: number, rate: number, dt: number) =>
  cur + (target - cur) * (1 - Math.exp(-rate * dt));

/** Common skeleton: plate + snaps at the pins. */
function base(p: Placement, color: string, plateH = PLATE_H) {
  const def = partDef(p.type);
  const len = def.length(p.props);
  const group = new THREE.Group();
  const span = len - 1;
  const pl = plate(len, color, plateH);
  group.add(pl);
  def.pins(p.props).forEach((i, pin) => {
    const s = snap();
    s.position.set(i - span / 2, plateH, 0);
    tagPin(s, pin);
    group.add(s);
  });
  return { group, len, span, plateH };
}

function textDecal(text: string, key: string, w: number, d: number, color = '#ffffff', size = 0.6) {
  const m = decal(
    (ctx, cw, ch) => {
      ctx.fillStyle = color;
      ctx.font = `900 ${Math.round(ch * size)}px system-ui, -apple-system, "Segoe UI", sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, cw / 2, ch / 2 + ch * 0.04);
    },
    key,
    w,
    d,
  );
  // Words should always read the right way up, whichever way the part is turned.
  m.userData.upright = true;
  return m;
}

const noop = () => {};

/** Mark an object (and its children) as snap `pin`, so touching it draws/stretches a wire. */
function tagPin(o: THREE.Object3D, pin: number) {
  o.traverse((c) => (c.userData.pin = pin));
}

// ───────────────────────────── Jumper cable ─────────────────────────────
function jumperView(p: Placement): PartView {
  const color = WIRE_COLORS[String(p.props.color)] ?? WIRE_COLORS.orange!;
  const group = new THREE.Group();
  const dx = Math.round(Number(p.props.dx ?? 1));
  const dy = Math.round(Number(p.props.dy ?? 1));
  const cableMat = new THREE.MeshStandardMaterial({ color, roughness: 0.38 });
  const capMat = plastic(color);
  const ends = [-1, 1].map((side, pin) => {
    const end = new THREE.Group();
    const cap = new THREE.Mesh(cylinder(0.27, 0.3, 0.14, 28), capMat);
    cap.position.y = 0.07;
    cap.castShadow = true;
    const sn = snap();
    sn.position.y = 0.14;
    end.add(cap, sn);
    end.position.set((side * dx) / 2, 0, (side * dy) / 2);
    tagPin(end, pin);
    group.add(end);
    return end;
  });
  const cable = new THREE.Mesh(new THREE.BufferGeometry(), cableMat);
  cable.castShadow = true;
  group.add(cable);
  let sig = '';
  const view: PartView = {
    group,
    emitters: [],
    update(pl, f) {
      // Each end sits on top of whatever is under it.
      const hs = [0, 1].map((i) => {
        if (!f.board) return 0;
        const cx = pl.x + (i ? dx : 0);
        const cy = pl.y + (i ? dy : 0);
        const below = f.board.stackAt(cx, cy, pl.id);
        const top = below.length ? below[below.length - 1]!.part.layer + 1 : 0;
        return (top - pl.layer) * LAYER_H;
      });
      const k = hs.join(',');
      if (k === sig) return;
      sig = k;
      ends.forEach((e, i) => (e.position.y = hs[i]!));
      const a = ends[0]!.position.clone().add(new THREE.Vector3(0, 0.12, 0));
      const b = ends[1]!.position.clone().add(new THREE.Vector3(0, 0.12, 0));
      const dist = a.distanceTo(b);
      const lift = 0.45 + dist * 0.14;
      const curve = new THREE.CubicBezierCurve3(
        a,
        a
          .clone()
          .lerp(b, 0.15)
          .add(new THREE.Vector3(0, lift, 0)),
        a
          .clone()
          .lerp(b, 0.85)
          .add(new THREE.Vector3(0, lift, 0)),
        b,
      );
      cable.geometry.dispose();
      cable.geometry = new THREE.TubeGeometry(curve, 48, 0.065, 10, false);
      view.flow = curve;
    },
    dispose: () => {
      cable.geometry.dispose();
      cableMat.dispose();
    },
  };
  view.update(p, { state: {}, reading: { current: 0, voltage: 0 }, input: {}, time: 0, dt: 0 });
  return view;
}

// ───────────────────────────── Battery ─────────────────────────────
function batteryView(p: Placement): PartView {
  const { group, span } = base(p, COLORS.battery);
  const holder = new THREE.Group();
  holder.position.y = PLATE_H;
  group.add(holder);

  const tray = new THREE.Mesh(slabGeometry(span - 0.75, 0.74, 0.12, 0.12), plastic('#262b4a'));
  tray.castShadow = true;
  holder.add(tray);

  const cellLen = span - 1.0;
  const cellMat = ownMaterial('#1d2238', { roughness: 0.25, metalness: 0.2 });
  const bandMat = plastic('#ffd23b');
  for (const side of [-1, 1]) {
    const cell = new THREE.Group();
    const body = new THREE.Mesh(cylinder(0.15, 0.15, cellLen, 28), cellMat);
    body.rotation.z = Math.PI / 2;
    const band = new THREE.Mesh(cylinder(0.153, 0.153, cellLen * 0.38, 28), bandMat);
    band.rotation.z = Math.PI / 2;
    band.position.x = side * cellLen * 0.22;
    const nub = new THREE.Mesh(cylinder(0.06, 0.06, 0.06, 16), chrome());
    nub.rotation.z = Math.PI / 2;
    nub.position.x = side * (cellLen / 2 + 0.03);
    body.castShadow = band.castShadow = true;
    cell.add(body, band, nub);
    cell.position.set(0, 0.12 + 0.15, side * 0.17);
    holder.add(cell);
  }
  const plus = textDecal('+', 'bat+', 0.5, 0.5, '#ffd23b', 0.9);
  plus.position.set(-span / 2 + 0.55, PLATE_H + 0.005, 0.24);
  const minus = textDecal('−', 'bat-', 0.5, 0.5, '#9fb0ff', 0.9);
  minus.position.set(span / 2 - 0.55, PLATE_H + 0.005, 0.24);
  const label = textDecal('3V', 'bat3v', 0.6, 0.3, '#ffffff', 0.8);
  label.position.set(0, PLATE_H + 0.005, -0.31);
  group.add(plus, minus, label);

  // Heat: a red glow and smoke puffs.
  const heatGlow = glowSprite('#ff4b1f');
  heatGlow.position.y = 0.5;
  group.add(heatGlow);
  const puffs: THREE.Sprite[] = [];
  for (let i = 0; i < 6; i++) {
    const s = new THREE.Sprite(
      new THREE.SpriteMaterial({
        map: heatGlow.material.map,
        color: '#9aa0b4',
        transparent: true,
        opacity: 0,
        depthWrite: false,
      }),
    );
    group.add(s);
    puffs.push(s);
  }

  return {
    group,
    emitters: [],
    update(_p, f) {
      const s = f.state as BatteryState;
      const heat = s.heat;
      cellMat.emissive.setRGB(heat * 0.9, heat * 0.15, 0);
      heatGlow.material.opacity = heat * 0.85;
      heatGlow.scale.setScalar(1.5 + heat * 2.2);
      // Wobble while shorted.
      const shake = s.shorted || heat > 0.6 ? heat * 0.05 : 0;
      holder.position.x = Math.sin(f.time * 55) * shake;
      holder.position.z = Math.cos(f.time * 47) * shake;
      puffs.forEach((pf, i) => {
        const cycle = (f.time * 0.6 + i / puffs.length) % 1;
        const on = heat > 0.4 ? (heat - 0.4) / 0.6 : 0;
        pf.position.set(Math.sin(i * 2.3 + f.time) * 0.3, 0.5 + cycle * 2, Math.cos(i * 1.7) * 0.25);
        pf.scale.setScalar(0.4 + cycle * 1.4);
        pf.material.opacity = on * (1 - cycle) * 0.55;
      });
    },
    dispose: () => cellMat.dispose(),
  };
}

// ───────────────────────────── Wire ─────────────────────────────
function wireView(p: Placement): PartView {
  const color = WIRE_COLORS[String(p.props.color)] ?? WIRE_COLORS.blue!;
  const len = partDef('wire').length(p.props);
  const h = 0.12;
  const { group, span } = base(p, color, h);
  // A shiny raised "trace" down the middle.
  const traceMat = new THREE.MeshStandardMaterial({
    color: '#ffffff',
    roughness: 0.25,
    transparent: true,
    opacity: 0.35,
  });
  const trace = new THREE.Mesh(slabGeometry(Math.max(0.3, span - 0.5), 0.2, 0.03, 0.08), traceMat);
  trace.position.y = h - 0.005;
  group.add(trace);
  const n = textDecal(String(len), `wire-n-${len}`, 0.32, 0.32, '#ffffff', 0.8);
  n.position.set(0, h + 0.03, 0);
  if (span > 1.5) group.add(n);
  return { group, emitters: [], update: noop, dispose: () => traceMat.dispose() };
}

// ───────────────────────────── Light bulb ─────────────────────────────
function lampView(p: Placement): PartView {
  const { group } = base(p, COLORS.lamp);
  const socket = new THREE.Mesh(cylinder(0.2, 0.24, 0.2, 28), chrome());
  socket.position.y = PLATE_H + 0.1;
  socket.castShadow = true;
  const glassMat = new THREE.MeshStandardMaterial({
    color: '#ffffff',
    roughness: 0.05,
    metalness: 0,
    transparent: true,
    opacity: 0.5,
    emissive: new THREE.Color('#ffb347'),
    emissiveIntensity: 0,
  });
  const bulb = new THREE.Mesh(sphere(0.34, 32, 24), glassMat);
  bulb.position.y = PLATE_H + 0.2 + 0.3;
  bulb.castShadow = true;
  const filMat = new THREE.MeshStandardMaterial({
    color: '#8a6a4a',
    emissive: new THREE.Color('#ffae42'),
    emissiveIntensity: 0,
  });
  const filament = new THREE.Mesh(new THREE.TorusGeometry(0.09, 0.015, 8, 24, Math.PI * 1.4), filMat);
  filament.position.copy(bulb.position);
  filament.rotation.x = Math.PI / 2;
  const glow = glowSprite('#ffc861');
  glow.position.copy(bulb.position);
  // A hot white-yellow core and a tight inner halo, so a lit bulb reads as ON even in daylight.
  const coreMat = new THREE.MeshBasicMaterial({
    color: '#fff7d1',
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const core = new THREE.Mesh(sphere(0.24, 24, 16), coreMat);
  core.position.copy(bulb.position);
  const halo = glowSprite('#fff1a8');
  halo.position.copy(bulb.position);
  group.add(socket, bulb, filament, core, glow, halo);
  const emitter: Emitter = { at: bulb, color: new THREE.Color('#ffbe5c'), intensity: 0 };
  const cold = new THREE.Color('#ffffff');
  const warm = new THREE.Color('#ffe066');
  return {
    group,
    emitters: [emitter],
    update(_p, f) {
      const b = (f.state as GlowState).brightness;
      const v = clamp01(b);
      glassMat.color.lerpColors(cold, warm, v);
      glassMat.emissive.set('#ffc93c');
      glassMat.emissiveIntensity = v * 3.2;
      glassMat.opacity = 0.5 + v * 0.45;
      filMat.emissiveIntensity = Math.min(4, b * 5);
      coreMat.opacity = v * 0.9;
      core.scale.setScalar(0.6 + v * 0.5);
      halo.material.opacity = Math.min(1, v * 1.3);
      halo.scale.setScalar(0.5 + v * 1.4);
      glow.material.opacity = Math.min(1, v * 1.1);
      glow.scale.setScalar(0.8 + v * 3.6);
      emitter.intensity = v;
    },
    dispose: () => {
      glassMat.dispose();
      filMat.dispose();
      coreMat.dispose();
      halo.material.dispose();
      glow.material.dispose();
    },
  };
}

// ───────────────────────────── LED ─────────────────────────────
function ledView(p: Placement): PartView {
  const { group, span } = base(p, COLORS.led);
  const c = LED_COLORS[(p.props.color as LedColor) ?? 'red'] ?? LED_COLORS.red;
  const holder = new THREE.Mesh(cylinder(0.2, 0.22, 0.1, 24), plastic('#30344d'));
  holder.position.y = PLATE_H + 0.05;
  const domeMat = new THREE.MeshStandardMaterial({
    color: c.hex,
    roughness: 0.08,
    transparent: true,
    opacity: 0.78,
    emissive: new THREE.Color(c.hex),
    emissiveIntensity: 0,
  });
  const domeBody = new THREE.Mesh(cylinder(0.16, 0.16, 0.2, 24), domeMat);
  domeBody.position.y = PLATE_H + 0.2;
  const domeTop = new THREE.Mesh(sphere(0.16, 24, 12), domeMat);
  domeTop.position.y = PLATE_H + 0.3;
  domeBody.castShadow = domeTop.castShadow = true;
  const glow = glowSprite(c.hex);
  glow.position.y = PLATE_H + 0.32;
  const plus = textDecal('+', 'led+', 0.4, 0.4, '#ff4b6e', 0.9);
  plus.position.set(-span / 2 + 0.5, PLATE_H + 0.005, 0.22);
  group.add(holder, domeBody, domeTop, glow, plus);
  const emitter: Emitter = { at: domeTop, color: new THREE.Color(c.hex), intensity: 0 };
  return {
    group,
    emitters: [emitter],
    update(_p, f) {
      const s = f.state as LedState;
      let v = clamp01(s.brightness);
      if (s.overdriven) v *= 0.75 + 0.25 * Math.sin(f.time * 40);
      domeMat.emissiveIntensity = v * 2.5;
      glow.material.opacity = Math.min(1, v * 1.2);
      glow.scale.setScalar(0.4 + v * 1.8);
      emitter.intensity = v * 0.6;
    },
    dispose: () => {
      domeMat.dispose();
      glow.material.dispose();
    },
  };
}

// ───────────────────────────── Motor + fan ─────────────────────────────
/** Max on-screen spin (rad/s): faster looks like strobing, so we blur instead. */
const MAX_VISUAL_SPIN = 22;

function makeFan(): {
  fan: THREE.Group;
  blades: THREE.Group;
  blur: THREE.Mesh;
  blurMat: THREE.MeshBasicMaterial;
} {
  const fan = new THREE.Group();
  const blades = new THREE.Group();
  const bladeMat = plastic(COLORS.fan, { rough: 0.25 });
  const hub = new THREE.Mesh(cylinder(0.11, 0.13, 0.12, 20), plastic('#ffffff'));
  hub.castShadow = true;
  blades.add(hub);
  for (let i = 0; i < 3; i++) {
    const arm = new THREE.Group();
    const blade = new THREE.Mesh(slabGeometry(0.62, 0.24, 0.035, 0.11), bladeMat);
    blade.position.x = 0.41;
    blade.rotation.x = 0.35; // pitch: this is what makes it fly!
    blade.castShadow = true;
    arm.add(blade);
    arm.rotation.y = (i * Math.PI * 2) / 3;
    blades.add(arm);
  }
  const blurMat = new THREE.MeshBasicMaterial({
    color: COLORS.fan,
    transparent: true,
    opacity: 0,
    depthWrite: false,
  });
  const blur = new THREE.Mesh(new THREE.CircleGeometry(0.74, 48).rotateX(-Math.PI / 2), blurMat);
  blur.position.y = 0.04;
  fan.add(blades, blur);
  return { fan, blades, blur, blurMat };
}

function motorView(p: Placement): PartView {
  const { group } = base(p, COLORS.motor);
  const canMat = new THREE.MeshStandardMaterial({ color: '#c9ced9', roughness: 0.32, metalness: 0.85 });
  const can = new THREE.Mesh(cylinder(0.3, 0.3, 0.42, 32), canMat);
  can.position.y = PLATE_H + 0.21;
  can.castShadow = true;
  const cap = new THREE.Mesh(cylinder(0.31, 0.31, 0.05, 32), plastic('#30344d'));
  cap.position.y = PLATE_H + 0.42;
  const shaft = new THREE.Mesh(cylinder(0.03, 0.03, 0.16, 12), chrome());
  shaft.position.y = PLATE_H + 0.5;
  const plus = textDecal('+', 'mot+', 0.4, 0.4, '#ffffff', 0.9);
  plus.position.set(-0.55, PLATE_H + 0.005, 0.24);
  group.add(can, cap, shaft, plus);

  const { fan, blades, blurMat } = makeFan();
  const fanRestY = PLATE_H + 0.56;
  fan.position.y = fanRestY;
  group.add(fan);

  let angle = 0;
  let landBounce = 0;
  let wasAttached = true;
  let flightSpin = 0;
  return {
    group,
    emitters: [],
    update(_p, f) {
      const s = f.state as MotorState;
      const omega = s.fanAttached ? s.omega : flightSpin;
      const vis = Math.sign(omega) * Math.min(Math.abs(omega), MAX_VISUAL_SPIN);
      angle += vis * f.dt;
      blades.rotation.y = angle;
      blurMat.opacity = clamp01((Math.abs(omega) - 12) / 40) * 0.45;
      can.position.x = Math.abs(s.omega) > 5 ? Math.sin(f.time * 90) * 0.004 : 0;

      if (!s.fanAttached) {
        if (wasAttached) flightSpin = s.launchOmega;
        flightSpin *= Math.exp(-0.35 * f.dt);
        const T = MOTOR.fanAwaySeconds;
        const u = clamp01(s.fanAway / T);
        const H = Math.min(9, Math.max(3.5, 6.5 * (s.launchOmega / 50) ** 2));
        fan.position.y = fanRestY + 4 * H * u * (1 - u);
        fan.position.x = Math.sin(u * Math.PI) * 0.9;
        fan.position.z = Math.sin(u * Math.PI * 2) * 0.35;
        fan.rotation.x = Math.sin(f.time * 7) * 0.25 * Math.sin(u * Math.PI);
        fan.rotation.z = Math.cos(f.time * 5) * 0.2 * Math.sin(u * Math.PI);
      } else {
        if (!wasAttached) landBounce = 1;
        landBounce = Math.max(0, landBounce - f.dt * 2.2);
        fan.position.set(0, fanRestY + Math.abs(Math.sin(landBounce * Math.PI * 2)) * landBounce * 0.5, 0);
        fan.rotation.x = fan.rotation.z = 0;
      }
      wasAttached = s.fanAttached;
    },
    dispose: () => {
      canMat.dispose();
      blurMat.dispose();
    },
  };
}

// ───────────────────────────── Switch ─────────────────────────────
function switchView(p: Placement): PartView {
  const { group } = base(p, COLORS.switch);
  const track = new THREE.Mesh(slabGeometry(1.0, 0.3, 0.07, 0.13), plastic('#2a2350'));
  track.position.y = PLATE_H;
  const knobMat = ownMaterial('#ffffff', { roughness: 0.25 });
  const knob = new THREE.Mesh(slabGeometry(0.42, 0.34, 0.22, 0.13), knobMat);
  knob.position.y = PLATE_H + 0.04;
  knob.castShadow = true;
  // One honest label that always says what the switch is doing right now.
  // Printed between the knob track and the plate edge, where the knob never covers it.
  const on = textDecal('ON', 'sw-on', 0.36, 0.16, '#7dffb4', 0.95);
  on.position.set(0, PLATE_H + 0.005, -0.325);
  const off = textDecal('OFF', 'sw-off', 0.36, 0.16, '#ffd6e4', 0.95);
  off.position.set(0, PLATE_H + 0.005, -0.325);
  group.add(track, knob, on, off);
  let x = p.props.on ? 0.26 : -0.26;
  const green = new THREE.Color('#3be08a');
  const white = new THREE.Color('#ffffff');
  return {
    group,
    emitters: [],
    update(pl, f) {
      const target = pl.props.on ? 0.26 : -0.26;
      x = damp(x, target, 28, f.dt);
      knob.position.x = x;
      knobMat.color.lerpColors(white, green, clamp01((x + 0.26) / 0.52));
      on.visible = !!pl.props.on;
      off.visible = !pl.props.on;
      // The label sits on the track, in the half the knob ISN'T covering, so nothing
      // stacked on the switch's ends can hide it, whichever way the switch is turned.
      for (const l of [on, off]) l.position.set(pl.props.on ? -0.24 : 0.24, PLATE_H + 0.075, 0);
    },
    dispose: () => knobMat.dispose(),
  };
}

// ───────────────────────────── Press button ─────────────────────────────
function buttonView(p: Placement): PartView {
  const { group } = base(p, COLORS.button);
  const housing = new THREE.Mesh(cylinder(0.32, 0.34, 0.12, 32), plastic('#2a2350'));
  housing.position.y = PLATE_H + 0.06;
  const capMat = ownMaterial('#ff2e63', { roughness: 0.22 });
  const cap = new THREE.Mesh(cylinder(0.25, 0.25, 0.16, 32), capMat);
  cap.castShadow = true;
  const capTop = new THREE.Mesh(
    (() => {
      const g = new THREE.SphereGeometry(0.25, 32, 8, 0, Math.PI * 2, 0, Math.PI / 5);
      g.translate(0, -0.25 * Math.cos(Math.PI / 5), 0);
      return g;
    })(),
    capMat,
  );
  capTop.position.y = 0.08;
  const capGroup = new THREE.Group();
  capGroup.add(cap, capTop);
  group.add(housing, capGroup);
  let y = 0;
  return {
    group,
    emitters: [],
    update(_p, f) {
      const target = f.input.pressed ? -0.07 : 0;
      y = damp(y, target, 40, f.dt);
      capGroup.position.y = PLATE_H + 0.16 + y;
      capMat.emissive.setRGB(f.input.pressed ? 0.25 : 0, 0, f.input.pressed ? 0.06 : 0);
    },
    dispose: () => capMat.dispose(),
  };
}

// ───────────────────────────── Buzzer ─────────────────────────────
function buzzerView(p: Placement): PartView {
  const { group } = base(p, COLORS.buzzer);
  const body = new THREE.Group();
  const disc = new THREE.Mesh(cylinder(0.36, 0.38, 0.2, 36), plastic('#22253a', { rough: 0.5 }));
  disc.castShadow = true;
  const hole = new THREE.Mesh(cylinder(0.07, 0.07, 0.02, 16), plastic('#05060a'));
  hole.position.y = 0.105;
  body.add(disc, hole);
  body.position.y = PLATE_H + 0.1;
  group.add(body);
  const rings: { m: THREE.Mesh; mat: THREE.MeshBasicMaterial }[] = [];
  for (let i = 0; i < 3; i++) {
    const mat = new THREE.MeshBasicMaterial({
      color: '#7dffe0',
      transparent: true,
      opacity: 0,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    const m = new THREE.Mesh(new THREE.RingGeometry(0.4, 0.46, 40).rotateX(-Math.PI / 2), mat);
    m.position.y = PLATE_H + 0.22;
    group.add(m);
    rings.push({ m, mat });
  }
  return {
    group,
    emitters: [],
    update(_p, f) {
      const s = f.state as BuzzerState;
      const lvl = s.level;
      body.position.x = lvl ? Math.sin(f.time * 120) * 0.012 * lvl : 0;
      rings.forEach((r, i) => {
        const c = (f.time * 1.6 + i / rings.length) % 1;
        r.m.scale.setScalar(1 + c * 1.6);
        r.mat.opacity = lvl ? (1 - c) * 0.6 * lvl : 0;
      });
    },
    dispose: () => rings.forEach((r) => r.mat.dispose()),
  };
}

// ───────────────────────────── Resistor ─────────────────────────────
const BAND_COLORS: Record<number, string[]> = {
  100: ['#8b4513', '#111111', '#8b4513'],
  1000: ['#8b4513', '#111111', '#ff2b2b'],
  10000: ['#8b4513', '#111111', '#ff8a1f'],
};
function resistorView(p: Placement): PartView {
  const { group, span } = base(p, COLORS.resistor);
  const body = new THREE.Group();
  const tube = new THREE.Mesh(cylinder(0.14, 0.14, 0.8, 24), plastic('#e8d3a8'));
  tube.rotation.z = Math.PI / 2;
  tube.castShadow = true;
  body.add(tube);
  const bands = BAND_COLORS[Number(p.props.ohms)] ?? BAND_COLORS[100]!;
  bands.forEach((c, i) => {
    const b = new THREE.Mesh(cylinder(0.145, 0.145, 0.07, 24), plastic(c));
    b.rotation.z = Math.PI / 2;
    b.position.x = -0.2 + i * 0.16;
    body.add(b);
  });
  for (const side of [-1, 1]) {
    const lead = new THREE.Mesh(cylinder(0.025, 0.025, span / 2 - 0.55, 8), chrome());
    lead.rotation.z = Math.PI / 2;
    lead.position.x = side * (0.4 + (span / 2 - 0.55) / 2);
    body.add(lead);
  }
  body.position.y = PLATE_H + 0.16;
  group.add(body);
  return { group, emitters: [], update: noop, dispose: noop };
}

export const VIEW_FACTORIES: Record<Placement['type'], (p: Placement) => PartView> = {
  battery: batteryView,
  wire: wireView,
  jumper: jumperView,
  lamp: lampView,
  led: ledView,
  motor: motorView,
  switch: switchView,
  button: buttonView,
  buzzer: buzzerView,
  resistor: resistorView,
};

export function createView(p: Placement): PartView {
  const v = VIEW_FACTORIES[p.type](p);
  if (!v.flow) {
    // Default electricity path: straight between the first and last snap, just above the plate.
    const def = partDef(p.type);
    const span = def.length(p.props) - 1;
    v.flow = new THREE.LineCurve3(
      new THREE.Vector3(-span / 2, PLATE_H + 0.1, 0),
      new THREE.Vector3(span / 2, PLATE_H + 0.1, 0),
    );
  }
  v.group.userData.partId = p.id;
  v.group.traverse((o) => {
    o.userData.partId = p.id;
    // Glows, halos, motion blur and sound rings are light, not plastic: taps go straight
    // through them (a lit bulb's glow must never steal the tap meant for the switch next to it).
    const mat = (o as THREE.Mesh).material as THREE.Material | undefined;
    const ghostly =
      (o as THREE.Sprite).isSprite ||
      (mat && !Array.isArray(mat) && mat.transparent && !mat.depthWrite && !o.userData.upright);
    if (ghostly) o.raycast = () => {};
  });
  return v;
}

/** Plate footprint size, for selection outlines. */
export function plateSize(p: Placement): { len: number; w: number } {
  if (p.type === 'jumper') return { len: 0.7, w: 0.7 };
  return { len: plateLength(partDef(p.type).length(p.props)), w: PLATE_W };
}

export { roundRect };
